// frontend-admin/src/modules/auth/InactivityManager.js
import { createLogger } from '@utils/logger.js';
import config from '@/config.js';

/**
 * InactivityManager tracks user activity and enforces inactivity timeout
 * @class InactivityManager
 */
export default class InactivityManager {
  constructor(authController) {
    this.logger = createLogger('InactivityManager');
    this.authController = authController;

    // Configuration
    this.inactivityTimeout = config.inactivityTimeout || 5 * 60 * 1000; // Default: 5 minutes
    this.warningTime = config.inactivityWarningTime || 30 * 1000; // Default: 30 seconds before timeout

    // State
    this.lastActivityTime = Date.now();
    this.lastHeartbeatTime = Date.now();
    this.inactivityTimer = null;
    this.warningTimer = null;
    this.isWarningShown = false;
    this.isActive = false;
    this.heartbeatInterval = 60 * 1000; // Send heartbeat every 60 seconds

    // Activity events to track
    this.activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Bind methods
    this.handleActivity = this.handleActivity.bind(this);
    this.showWarning = this.showWarning.bind(this);
    this.forceLogout = this.forceLogout.bind(this);
  }

  /**
   * Start monitoring user activity
   */
  start() {
    if (this.isActive) return;

    this.isActive = true;
    this.lastActivityTime = Date.now();

    // Add event listeners for activity detection
    this.activityEvents.forEach(event => {
      document.addEventListener(event, this.handleActivity, { passive: true });
    });

    // Start inactivity check
    this.startInactivityTimer();

    this.logger.info(`Inactivity monitoring started (timeout: ${this.inactivityTimeout / 1000 / 60} minutes)`);
  }

  /**
   * Stop monitoring user activity
   */
  stop() {
    if (!this.isActive) return;

    this.isActive = false;

    // Remove event listeners
    this.activityEvents.forEach(event => {
      document.removeEventListener(event, this.handleActivity);
    });

    // Clear timers
    this.clearTimers();

    this.logger.info('Inactivity monitoring stopped');
  }

  /**
   * Handle user activity
   * @private
   */
  handleActivity() {
    if (!this.isActive) return;

    const now = Date.now();
    const timeSinceLastActivity = now - this.lastActivityTime;

    // Only update if more than 1 second since last activity (debounce)
    if (timeSinceLastActivity > 1000) {
      this.lastActivityTime = now;

      // Hide warning if shown
      if (this.isWarningShown) {
        this.hideWarning();
      }

      // Restart inactivity timer
      this.startInactivityTimer();

      // Resume token auto-refresh if it was paused
      if (this.authController.tokenManager) {
        this.authController.tokenManager.resumeAutoRefresh();
      }

      // Send heartbeat to backend (debounced - only every minute)
      const timeSinceLastHeartbeat = now - this.lastHeartbeatTime;
      if (timeSinceLastHeartbeat > this.heartbeatInterval) {
        this.sendHeartbeat();
      }
    }
  }

  /**
   * Send activity heartbeat to backend
   * @private
   */
  async sendHeartbeat() {
    try {
      this.lastHeartbeatTime = Date.now();

      const response = await fetch('/api/auth/activity', {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) {
        this.logger.warn('Failed to send activity heartbeat');
      }
    } catch (error) {
      this.logger.error('Error sending activity heartbeat:', error);
    }
  }

  /**
   * Start the inactivity timer
   * @private
   */
  startInactivityTimer() {
    this.clearTimers();

    // Set timer to show warning
    const warningDelay = this.inactivityTimeout - this.warningTime;
    this.warningTimer = setTimeout(this.showWarning, warningDelay);

    // Set timer to force logout
    this.inactivityTimer = setTimeout(this.forceLogout, this.inactivityTimeout);
  }

  /**
   * Clear all timers
   * @private
   */
  clearTimers() {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }

    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
  }

  /**
   * Show inactivity warning to user
   * @private
   */
  showWarning() {
    if (!this.isActive || this.isWarningShown) return;

    this.isWarningShown = true;
    const secondsRemaining = Math.ceil(this.warningTime / 1000);

    this.logger.warn(`Inactivity warning: ${secondsRemaining} seconds until automatic logout`);

    // Create warning overlay
    const overlay = document.createElement('div');
    overlay.id = 'inactivity-warning-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.7);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
    `;

    const modal = document.createElement('div');
    modal.style.cssText = `
      background: white;
      padding: 30px;
      border-radius: 8px;
      max-width: 400px;
      text-align: center;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    `;

    modal.innerHTML = `
      <h2 style="margin: 0 0 15px 0; color: #d32f2f;">Session Expiring</h2>
      <p style="margin: 0 0 20px 0; font-size: 16px; color: #333;">
        You will be logged out in <strong id="countdown">${secondsRemaining}</strong> seconds due to inactivity.
      </p>
      <button id="stay-logged-in-btn" style="
        background: #1976d2;
        color: white;
        border: none;
        padding: 12px 24px;
        border-radius: 4px;
        font-size: 16px;
        cursor: pointer;
        transition: background 0.3s;
      " onmouseover="this.style.background='#1565c0'" onmouseout="this.style.background='#1976d2'">
        Stay Logged In
      </button>
    `;

    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    // Update countdown
    const countdownElement = document.getElementById('countdown');
    let remaining = secondsRemaining;
    const countdownInterval = setInterval(() => {
      remaining--;
      if (countdownElement) {
        countdownElement.textContent = remaining;
      }
      if (remaining <= 0 || !this.isWarningShown) {
        clearInterval(countdownInterval);
      }
    }, 1000);

    // "Stay Logged In" button
    const stayButton = document.getElementById('stay-logged-in-btn');
    if (stayButton) {
      stayButton.addEventListener('click', () => {
        this.handleActivity(); // Registers activity and hides warning
      });
    }

    // Clicking overlay also counts as activity
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        this.handleActivity();
      }
    });

    // Pause token auto-refresh during warning
    if (this.authController.tokenManager) {
      this.authController.tokenManager.pauseAutoRefresh();
    }
  }

  /**
   * Hide the inactivity warning
   * @private
   */
  hideWarning() {
    if (!this.isWarningShown) return;

    this.isWarningShown = false;
    const overlay = document.getElementById('inactivity-warning-overlay');
    if (overlay) {
      overlay.remove();
    }

    this.logger.info('Inactivity warning dismissed');
  }

  /**
   * Force logout due to inactivity
   * @private
   */
  async forceLogout() {
    if (!this.isActive) return;

    this.logger.warn('Forcing logout due to inactivity');

    // Stop monitoring
    this.stop();

    // Hide warning if shown
    this.hideWarning();

    // Show logout message
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10001;
    `;

    const modal = document.createElement('div');
    modal.style.cssText = `
      background: white;
      padding: 30px;
      border-radius: 8px;
      max-width: 400px;
      text-align: center;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    `;

    modal.innerHTML = `
      <h2 style="margin: 0 0 15px 0; color: #d32f2f;">Session Expired</h2>
      <p style="margin: 0 0 20px 0; font-size: 16px; color: #333;">
        You have been logged out due to inactivity.
      </p>
      <p style="margin: 0; font-size: 14px; color: #666;">
        Redirecting to login page...
      </p>
    `;

    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    // Perform logout
    setTimeout(async () => {
      try {
        await this.authController.logout();
      } catch (error) {
        this.logger.error('Error during forced logout:', error);
        // Force redirect even if logout fails
        window.location.href = this.authController.websiteUrl || '/';
      }
    }, 2000);
  }

  /**
   * Get time remaining until inactivity timeout (in milliseconds)
   * @returns {number}
   */
  getTimeRemaining() {
    if (!this.isActive) return 0;

    const timeSinceActivity = Date.now() - this.lastActivityTime;
    return Math.max(0, this.inactivityTimeout - timeSinceActivity);
  }

  /**
   * Get time remaining in human-readable format
   * @returns {string}
   */
  getTimeRemainingFormatted() {
    const ms = this.getTimeRemaining();
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  }
}
